import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../api.ts'

/**
 * Price-list import wizard (SPEC 5.1): upload → map columns → preview → save.
 * The preview exists because "which column is the price" is the question that,
 * answered wrongly, sells machines at cost.
 */

interface Field {
  key: string
  label: string
  required: boolean
}
interface Analysis {
  sheetNames: string[]
  sheet: string
  headerRow: number
  columns: { index: number; header: string; sample: string[] }[]
  totalRows: number
  suggested: Record<string, string | null>
}
interface PreviewRow {
  rowNumber: number
  values: Record<string, string>
  action: 'nowa' | 'aktualizacja' | 'blad'
  problems: string[]
}
interface Preview {
  rows: PreviewRow[]
  willCreate: number
  willUpdate: number
  withErrors: number
}
interface Manufacturer {
  id: string
  name: string
}

type Step = 'plik' | 'mapowanie' | 'podglad' | 'gotowe'

export function ImportPage() {
  const nav = useNavigate()
  const [step, setStep] = useState<Step>('plik')
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([])
  const [manufacturerId, setManufacturerId] = useState('')
  const [fields, setFields] = useState<Field[]>([])
  const [fileB64, setFileB64] = useState('')
  const [filename, setFilename] = useState('')
  const [analysis, setAnalysis] = useState<Analysis | null>(null)
  const [mapping, setMapping] = useState<Record<string, string | null>>({})
  const [preview, setPreview] = useState<Preview | null>(null)
  const [result, setResult] = useState<{ created: number; updated: number; skipped: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<{ manufacturers: Manufacturer[] }>('/api/manufacturers').then((r) => setManufacturers(r.manufacturers)).catch(() => {})
    api.get<{ fields: Field[] }>('/api/import/fields').then((r) => setFields(r.fields)).catch(() => {})
  }, [])

  async function onFile(f: File) {
    setError(null)
    setBusy(true)
    try {
      const b64 = await fileToBase64(f)
      setFileB64(b64)
      setFilename(f.name)
      const r = await api.post<{ analysis: Analysis }>('/api/import/analyse', { file: b64, filename: f.name })
      setAnalysis(r.analysis)
      setMapping(r.analysis.suggested)
      setStep('mapowanie')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się odczytać pliku')
    } finally {
      setBusy(false)
    }
  }

  async function doPreview() {
    if (!analysis) return
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<{ preview: Preview }>('/api/import/preview', {
        file: fileB64,
        filename,
        manufacturerId,
        sheet: analysis.sheet,
        headerRow: analysis.headerRow,
        mapping,
      })
      setPreview(r.preview)
      setStep('podglad')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  async function doCommit() {
    if (!analysis) return
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<{ result: { created: number; updated: number; skipped: number } }>('/api/import/commit', {
        file: fileB64,
        filename,
        manufacturerId,
        sheet: analysis.sheet,
        headerRow: analysis.headerRow,
        mapping,
      })
      setResult(r.result)
      setStep('gotowe')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  const mappedFields = new Set(Object.values(mapping).filter(Boolean) as string[])
  const missingRequired = fields.filter((f) => f.required && !mappedFields.has(f.key))

  return (
    <div className="p-6 max-w-4xl">
      <Link to="/katalog" className="text-sm text-slate-500 hover:text-slate-700">
        ← Katalog
      </Link>
      <h1 className="text-xl font-bold text-slate-800 mt-2 mb-1">Wgraj cennik</h1>
      <p className="text-sm text-slate-500 mb-5">
        Wgranie tego samego cennika drugi raz zaktualizuje ceny i nie utworzy duplikatów.
      </p>

      <Steps current={step} />

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 my-3">
          {error}
        </div>
      )}

      {/* 1 — file + manufacturer */}
      {step === 'plik' && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Producent</label>
            <select
              value={manufacturerId}
              onChange={(e) => setManufacturerId(e.target.value)}
              className="w-full max-w-sm px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            >
              <option value="">— wybierz —</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">Cennik dotyczy jednego producenta.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Plik cennika (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={!manufacturerId || busy}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void onFile(f)
              }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-600 file:text-white hover:file:bg-brand-700 file:cursor-pointer disabled:opacity-50"
            />
            {!manufacturerId && <p className="text-xs text-amber-600 mt-1">Najpierw wybierz producenta.</p>}
          </div>
        </div>
      )}

      {/* 2 — column mapping */}
      {step === 'mapowanie' && analysis && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mt-4">
          <div className="flex justify-between items-baseline mb-3">
            <h2 className="font-semibold text-slate-700">Wskaż, co oznacza która kolumna</h2>
            <span className="text-xs text-slate-400">
              arkusz „{analysis.sheet}", nagłówki w wierszu {analysis.headerRow}, {analysis.totalRows} wierszy
            </span>
          </div>

          {missingRequired.length > 0 && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              Brakuje wymaganych pól: {missingRequired.map((f) => f.label).join(', ')}
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-200">
                <th className="pb-1 font-medium">Kolumna w pliku</th>
                <th className="pb-1 font-medium">Przykładowe wartości</th>
                <th className="pb-1 font-medium">To jest…</th>
              </tr>
            </thead>
            <tbody>
              {analysis.columns.map((c) => (
                <tr key={c.index} className="border-b border-slate-50 last:border-0">
                  <td className="py-1.5 font-medium text-slate-700">{c.header}</td>
                  <td className="py-1.5 text-slate-400 text-xs truncate max-w-64">{c.sample.join(' · ') || '—'}</td>
                  <td className="py-1.5">
                    <select
                      value={mapping[String(c.index)] ?? ''}
                      onChange={(e) =>
                        setMapping({ ...mapping, [String(c.index)]: e.target.value || null })
                      }
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                    >
                      <option value="">— pomiń —</option>
                      {fields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                          {f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex gap-2 mt-4">
            <button onClick={() => setStep('plik')} className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700">
              Wstecz
            </button>
            <button
              onClick={() => void doPreview()}
              disabled={busy || missingRequired.length > 0}
              className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'Sprawdzam…' : 'Pokaż podgląd'}
            </button>
          </div>
        </div>
      )}

      {/* 3 — preview */}
      {step === 'podglad' && preview && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mt-4">
          <h2 className="font-semibold text-slate-700 mb-3">Podgląd — nic jeszcze nie zapisano</h2>

          <div className="flex gap-4 text-sm mb-4">
            <Stat label="nowych maszyn" value={preview.willCreate} tone="green" />
            <Stat label="aktualizacji" value={preview.willUpdate} tone="blue" />
            {preview.withErrors > 0 && <Stat label="wierszy z błędem (pominę)" value={preview.withErrors} tone="red" />}
          </div>

          <div className="max-h-96 overflow-auto border border-slate-100 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-1 font-medium">Wiersz</th>
                  <th className="px-2 py-1 font-medium">Co się stanie</th>
                  <th className="px-2 py-1 font-medium">Kod</th>
                  <th className="px-2 py-1 font-medium">Model</th>
                  <th className="px-2 py-1 font-medium">Uwagi</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 200).map((r) => (
                  <tr key={r.rowNumber} className="border-b border-slate-50 last:border-0">
                    <td className="px-2 py-1 text-slate-400">{r.rowNumber}</td>
                    <td className="px-2 py-1">
                      <ActionBadge action={r.action} />
                    </td>
                    <td className="px-2 py-1 font-mono">{r.values.catalogCode ?? '—'}</td>
                    <td className="px-2 py-1">{r.values.model ?? '—'}</td>
                    <td className="px-2 py-1 text-red-600">{r.problems.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rows.length > 200 && (
            <p className="text-xs text-slate-400 mt-1">Pokazuję pierwsze 200 z {preview.rows.length} wierszy.</p>
          )}

          <div className="flex gap-2 mt-4">
            <button onClick={() => setStep('mapowanie')} className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700">
              Wstecz
            </button>
            <button
              onClick={() => void doCommit()}
              disabled={busy || preview.willCreate + preview.willUpdate === 0}
              className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'Zapisuję…' : `Zapisz (${preview.willCreate + preview.willUpdate})`}
            </button>
          </div>
        </div>
      )}

      {/* 4 — done */}
      {step === 'gotowe' && result && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mt-4">
          <h2 className="font-semibold text-green-700 mb-2">✔ Cennik wgrany</h2>
          <p className="text-sm text-slate-600">
            Dodano <strong>{result.created}</strong>, zaktualizowano <strong>{result.updated}</strong>
            {result.skipped > 0 && <>, pominięto <strong>{result.skipped}</strong> (błędy)</>}.
          </p>
          <button
            onClick={() => nav('/katalog')}
            className="mt-4 text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700"
          >
            Zobacz katalog
          </button>
        </div>
      )}
    </div>
  )
}

function Steps({ current }: { current: Step }) {
  const all: [Step, string][] = [
    ['plik', '1. Plik'],
    ['mapowanie', '2. Kolumny'],
    ['podglad', '3. Podgląd'],
    ['gotowe', '4. Gotowe'],
  ]
  const idx = all.findIndex(([s]) => s === current)
  return (
    <div className="flex gap-1 text-xs">
      {all.map(([s, label], i) => (
        <div
          key={s}
          className={`px-3 py-1 rounded ${
            i < idx ? 'bg-brand-100 text-brand-700' : i === idx ? 'bg-brand-600 text-white font-medium' : 'bg-slate-100 text-slate-400'
          }`}
        >
          {label}
        </div>
      ))}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'green' | 'blue' | 'red' }) {
  const tones = { green: 'text-green-700 bg-green-50', blue: 'text-blue-700 bg-blue-50', red: 'text-red-700 bg-red-50' }
  return (
    <div className={`px-3 py-1.5 rounded ${tones[tone]}`}>
      <span className="font-bold">{value}</span> <span className="text-xs">{label}</span>
    </div>
  )
}

function ActionBadge({ action }: { action: PreviewRow['action'] }) {
  const map = {
    nowa: 'bg-green-100 text-green-700',
    aktualizacja: 'bg-blue-100 text-blue-700',
    blad: 'bg-red-100 text-red-700',
  }
  const label = { nowa: 'nowa', aktualizacja: 'aktualizacja', blad: 'błąd' }
  return <span className={`px-1.5 py-0.5 rounded ${map[action]}`}>{label[action]}</span>
}

/** File → base64 without the data: prefix. */
function fileToBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => {
      const s = String(r.result)
      resolve(s.slice(s.indexOf(',') + 1))
    }
    r.onerror = () => reject(new Error('Nie udało się odczytać pliku'))
    r.readAsDataURL(f)
  })
}
